import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import {
  formatNumberBasedOnValue,
  returnSizeFormatted,
} from "@/lib/globalFormatterHelper";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { QuoteSide } from "@/lib/interfaces and Enums/ENUMQuoteSide";
import { getBestCounterparty } from "../actionSummaryMainTableColDefs";
import tradeDataStore from "@/store/tradeDataStore";
import {
  hasPendingChanges,
  getFillsFromStore,
  getLegSummaryDataFromStore,
  calculateBlendedPriceByType,
  calculateTotalQuantityByType,
  calculateFilledPercentByType,
  formatPriceWithNotation,
} from "../cellRenderers/FillsSummary/fillsSummaryHelpers";
import {
  containsHyphenInQuote,
  convertToDollarFraction,
  formatDollarFractionWithZValueText,
} from "@/lib/businessRules/dollarFractionBusinessRule";
import { isQuoteLockedAtAwarded } from "../columnHelpers/cellCanBeEdited";

export const QUOTE_LOCKED_AT_AWARDED_TOOLTIP = "Go Off-Quote to adjust";

// LQC-1733: message sits below the yield (.ag-tooltip is pre-line in globals.css).
export function displayQuoteCellTooltip(params: any) {
  const yieldTooltip = displayYieldTooltipForAllQuoteLevels(params);
  if (!isQuoteLockedAtAwarded(params)) return yieldTooltip;
  return yieldTooltip
    ? `${yieldTooltip}\n${QUOTE_LOCKED_AT_AWARDED_TOOLTIP}`
    : QUOTE_LOCKED_AT_AWARDED_TOOLTIP;
}

export function displayYieldTooltipForAllQuoteLevels(params: any) {
  const { data, currentField, isES, isLeg, isOp, isOutright, isFRN} =
    returnAllTradeDetailsFromGenericParams(params);

  if (data.status == ExecutionStrategyStatus.New) {
    // Nothing to check when at status New - Was creating a tooltip of NaN. // Secondary return later for NaN - No longer getting hit.
    return; // Early return
  }

  let tooltipToDisplay = "";

  let notationIsDollarP = false;
  let notationIsDollarFr = false;
  let notationIsYield = false;
  let notationIsBps = false;

  // Check all possible levels of notation depending on where TT is called from.,'
  if (data.notationER) {
    notationIsDollarP = data.notationER == "$p";
    notationIsDollarFr = data.notationER == "$fr";
    notationIsYield = data.notationER == "%";
    notationIsBps = data.notationER == "bps";
  } else if (data.notationES) {
    notationIsDollarP = data.notationES == "$p";
    notationIsDollarFr = data.notationES == "$fr";
    notationIsYield = data.notationES == "%";
    notationIsBps = data.notationES == "bps";
  } else {
    notationIsDollarP = data.notation == "$p";
    notationIsDollarFr = data.notation == "$fr";
    notationIsYield = data.notation == "%";
    notationIsBps = data.notation == "bps";
  }

  if (!notationIsDollarFr && !notationIsDollarP && !notationIsYield && !notationIsBps || (notationIsBps && !isFRN)) {
    return; // return early
  }

  // If the function is called from the OP at the ES level, need to navigate through one more layer of nested data:

  if (
    isOp &&
    isES &&
    data.executionRequestCounterParties &&
    data.executionRequestCounterParties.length > 0
  ) {
    const bestCounterparty = getBestCounterparty(params.data);
    if (!isOutright) {
      return; // return early
    }
    switch (currentField) {
      case "bid":
        if(notationIsYield || notationIsBps) {
          tooltipToDisplay = bestCounterparty?.legs?.[0]?.bidPrice;
        } else {
          tooltipToDisplay = bestCounterparty?.legs?.[0]?.bidYield; 
        }
        break;
      case "ask":
        if(notationIsYield || notationIsBps) {
          tooltipToDisplay = bestCounterparty?.legs?.[0]?.askPrice;
        } else {
          tooltipToDisplay = bestCounterparty?.legs?.[0]?.askYield;
        }
        break;
      case "mid":
        if(notationIsYield || notationIsBps) {
          tooltipToDisplay = bestCounterparty?.legs?.[0]?.midPrice;
        } else {
          tooltipToDisplay = bestCounterparty?.legs?.[0]?.midYield;
        }
        
        break;
      default:
    }
  } else if (data.legs?.length > 0) {
    // Handle all other situtaions for yield.
    if (!isOutright) {
      return; // return early
    }
    switch (currentField) {
      case "bid":
        tooltipToDisplay = notationIsYield || notationIsBps ? data.legs[0].bidPrice : data.legs[0].bidYield;
        break;
      case "ask":
        tooltipToDisplay = notationIsYield || notationIsBps ? data.legs[0].askPrice : data.legs[0].askYield;
        break;
      case "mid":
        tooltipToDisplay = data.legs[0].midYield;
        break;
      default:
    }
  } else if (isLeg) {
    // NO check for outright - ALL legs are allowed to show the yields/prices.
    switch (currentField) {
      case "bid":
        tooltipToDisplay = notationIsYield || notationIsBps ? data.bidPrice : data.bidYield;
        break;
      case "ask":
        tooltipToDisplay = notationIsYield || notationIsBps ? data.askPrice : data.askYield;
        break;
      case "mid":
        tooltipToDisplay = data.midYield;
        break;
      default:
    }
  }

  const convertedToolTipToPercent = Number(tooltipToDisplay); // This is showing with 8dp. Do we want to change to 6?
  if (Number.isNaN(convertedToolTipToPercent)) {
    console.log("prevented NAN");
    return "";
  }

  if (convertedToolTipToPercent == 0) {
    return "";
  }

  let numberOfDecimalPlaces
  if(notationIsYield || notationIsBps) {
    numberOfDecimalPlaces = formatNumberBasedOnValue("$p");
      return `${parseFloat(convertedToolTipToPercent.toFixed(numberOfDecimalPlaces))}`;
 
  } else {
    numberOfDecimalPlaces = formatNumberBasedOnValue("%"); // Get the current decimal rounding for yields %
    return `${parseFloat(convertedToolTipToPercent.toFixed(numberOfDecimalPlaces))} %`; // Always returning a % despite notation different to % as this is a conversion.
  }

}

/**
 * Displays a yield tooltip for fill price cells.
 * Uses the parent block's quoteSide to determine which yield (bid/ask) to show.
 * Only displays when notation is $p or $fr (same logic as displayYieldTooltipForAllQuoteLevels).
 */
export function displayYieldTooltipForFills(params: any) {
  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);

  if (!fullTradeData || !params.data?.blockId) return;

  const esid = fullTradeData.esid;
  const blockId = params.data.blockId;

  // Get the parent leg from the store
  const tradeData = tradeDataStore.getState().tradeData;
  const trade = tradeData.find((t: any) => t.esid === esid);
  if (!trade) return;

  let leg: any;
  if (trade.role === "RP" && trade.legs) {
    leg = trade.legs.find((l: any) => l.blockId === blockId);
  } else if (trade.role === "OP" && trade.executionRequestCounterParties) {
    for (const cp of trade.executionRequestCounterParties) {
      if (cp.legs) {
        leg = cp.legs.find((l: any) => l.blockId === blockId);
        if (leg) break;
      }
    }
  }

  if (!leg) return;

  // For RP, show "After update:" tooltip when there are pending fill changes
  if (trade.role === "RP" && leg.fills && hasPendingChanges(leg.fills)) {
    const fills = getFillsFromStore(esid, blockId);
    const pendingPrice = calculateBlendedPriceByType(fills, "pending");
    const legData = getLegSummaryDataFromStore(esid, blockId);
    let pendingValue = formatPriceWithNotation(pendingPrice, legData.notation);

    // For $fr, show Z-value as Unicode fraction in tooltip (e.g., "116-023 $fr" → "116-02⅜ $fr")
    if (legData.notation === "$fr" && pendingPrice !== null) {
      let fractionStr: string | null = null;
      if (typeof pendingPrice === "string" && containsHyphenInQuote(pendingPrice)) {
        fractionStr = pendingPrice;
      } else if (typeof pendingPrice === "number") {
        const converted = convertToDollarFraction(pendingPrice);
        if (converted.includes("-")) {
          fractionStr = converted;
        }
      }
      if (fractionStr) {
        pendingValue = `${formatDollarFractionWithZValueText(fractionStr)} $fr`;
      }
    }

    return pendingValue
      ? `After update: ${pendingValue}`
      : "After update: (no price set)";
  }

  // Only show tooltip for $p or $fr notation
  const notation = leg.notation;
  if (notation !== "$p" && notation !== "$fr") return;

  // Use quoteSide to determine which yield to show
  const quoteSide = leg.quoteSide;
  let tooltipToDisplay: string | number | undefined;

  if (quoteSide === QuoteSide.Bid) {
    tooltipToDisplay = leg.bidYield;
  } else if (quoteSide === QuoteSide.Ask) {
    tooltipToDisplay = leg.askYield;
  } else {
    return; // Two-Way/Mid/Both - no single yield to show
  }

  const convertedToPercent = Number(tooltipToDisplay);
  if (Number.isNaN(convertedToPercent) || convertedToPercent === 0) return "";

  const numberOfDecimalPlaces = formatNumberBasedOnValue("%");
  return `${parseFloat(convertedToPercent.toFixed(numberOfDecimalPlaces))} %`;
}

/**
 * Displays "After update:" tooltip for fill quantity at the leg level.
 * Only shows when there are pending fill changes (RP only).
 */
export function displayPendingTooltipForFillQuantity(params: any) {
  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);
  if (!fullTradeData || !params.data?.blockId) return;

  const esid = fullTradeData.esid;
  const blockId = params.data.blockId;

  const fills = getFillsFromStore(esid, blockId);
  if (!hasPendingChanges(fills)) return;

  const pendingQuantity = calculateTotalQuantityByType(fills, "pending");
  const instrumentType =
    params.data?.instrumentType ||
    getLegSummaryDataFromStore(esid, blockId).instrumentType;

  const pendingValue =
    pendingQuantity > 0
      ? returnSizeFormatted(pendingQuantity, instrumentType)
      : "";

  return pendingValue
    ? `After update: ${pendingValue}`
    : "After update: 0";
}

/**
 * Displays "After update:" tooltip for filled percent at the leg level.
 * Only shows when there are pending fill changes (RP only).
 */
export function displayPendingTooltipForFilledPercent(params: any) {
  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);
  if (!fullTradeData || !params.data?.blockId) return;

  const esid = fullTradeData.esid;
  const blockId = params.data.blockId;
  const blockNotional = params.data?.notionalLeg1;

  if (!blockNotional) return;

  const fills = getFillsFromStore(esid, blockId);
  if (!hasPendingChanges(fills)) return;

  const pendingPercent = calculateFilledPercentByType(
    fills,
    blockNotional,
    "pending",
  );

  return `After update: ${Math.round(pendingPercent)}%`;
}
